import { SalesData, ForecastData, ForecastProduct, MonthlySummary, OverallSummary } from '../types';
import { FilterType } from '../components/SearchFilter';
import { calculateYOY } from './mathUtils';

/**
 * Loads default sales data from the provided CSV file
 * @returns Promise that resolves to an array of SalesData objects
 */
export const loadDefaultSalesData = async (): Promise<SalesData[]> => {
  try {
    // Use the specific filename requested by the user
    const response = await fetch('/Product Sales & Units by Month - 2022-01-01 - 2025-04-21.csv');
    if (!response.ok) {
      // Fallback to the default name if the specific file is not found
      const fallbackResponse = await fetch('/default-sales-data.csv');
      if (!fallbackResponse.ok) {
        throw new Error('Failed to load default data');
      }
      const csvText = await fallbackResponse.text();
      return parseCSVText(csvText);
    }
    const csvText = await response.text();
    return parseCSVText(csvText);
  } catch (error) {
    console.error('Error loading default data:', error);
    return [];
  }
};

/**
 * Parses a CSV text string into SalesData objects
 * @param csvText The CSV text to parse
 * @returns Array of SalesData objects
 */
export const parseCSVText = (csvText: string): SalesData[] => {
  try {
    if (!csvText) {
      throw new Error('CSV text is empty');
    }
    
    console.log('CSV content:', csvText.substring(0, 200) + '...');
    
    // Split by lines and process (handle different line endings)
    const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
    
    if (lines.length === 0) {
      throw new Error('CSV text is empty');
    }
    
    console.log(`Found ${lines.length} lines in CSV`);
    console.log('First line:', lines[0]);
    
    // Check if this is the Shopify sales report format
    const isShopifyFormat = lines[0].includes('Month') && 
                           lines[0].includes('Product variant SKU') && 
                           lines[0].includes('Quantity ordered');

    // Get headers (and normalize them)
    // Try to handle both comma and semicolon delimiters
    const delimiter = lines[0].includes(',') ? ',' : (lines[0].includes(';') ? ';' : ',');
    console.log(`Using delimiter: "${delimiter}"`);
    
    // Special handling for quoted values in header
    let headers: string[];
    if (lines[0].includes('"')) {
      // Handle quoted headers
      const headerLine = lines[0];
      headers = [];
      let currentHeader = '';
      let inQuote = false;
      
      for (let i = 0; i < headerLine.length; i++) {
        const char = headerLine[i];
        
        if (char === '"') {
          inQuote = !inQuote;
        } else if (char === delimiter && !inQuote) {
          headers.push(currentHeader.replace(/"/g, '').trim().toLowerCase().replace(/\s+/g, '_'));
          currentHeader = '';
        } else {
          currentHeader += char;
        }
      }
      
      // Add the last header
      if (currentHeader) {
        headers.push(currentHeader.replace(/"/g, '').trim().toLowerCase().replace(/\s+/g, '_'));
      }
    } else {
      // Simple header parsing
      headers = lines[0].split(delimiter).map(header => 
        header.trim().toLowerCase().replace(/\s+/g, '_').replace(/"/g, '')
      );
    }
    
    console.log('Parsed headers:', headers);
    
    // Map CSV columns to our data model (try alternative column names)
    const findColumnIndex = (possibleNames: string[]) => {
      for (const name of possibleNames) {
        const index = headers.indexOf(name);
        if (index !== -1) return index;
      }
      return -1;
    };
    
    // Set up column mapping based on detected format
    let columnMap = {
      sku: -1,
      productName: -1,
      period: -1,
      salesQuantity: -1,
      salesRevenue: -1,
      platform: -1
    };
    
    if (isShopifyFormat) {
      console.log('Detected Shopify sales report format');
      columnMap = {
        sku: findColumnIndex(['product_variant_sku', 'sku', 'product_sku']),
        productName: findColumnIndex(['product_title_at_time_of_sale', 'product_title', 'product']),
        period: findColumnIndex(['month', 'date', 'period']),
        salesQuantity: findColumnIndex(['quantity_ordered', 'quantity', 'qty', 'units_sold']),
        salesRevenue: findColumnIndex(['total_sales', 'sales', 'revenue', 'total']),
        // Use a default platform since Shopify exports might not have it
        platform: -1
      };
    } else {
      // Generic format
      columnMap = {
        sku: findColumnIndex(['sku', 'product_sku', 'product_id', 'id', 'item_number']),
        productName: findColumnIndex(['product_name', 'product', 'item_name', 'name', 'title']),
        period: findColumnIndex(['period', 'date', 'month', 'year_month', 'time_period']),
        salesQuantity: findColumnIndex(['sales_quantity', 'quantity', 'qty', 'units', 'units_sold']),
        salesRevenue: findColumnIndex(['sales_revenue', 'revenue', 'sales', 'amount', 'total']),
        platform: findColumnIndex(['platform', 'channel', 'marketplace', 'store', 'source'])
      };
    }
    
    console.log('Column mapping:', columnMap);
    
    // Validate required columns
    const missingColumns = Object.entries(columnMap)
      .filter(([key, index]) => index === -1 && key !== 'platform') // platform can be optional
      .map(([key]) => key);
      
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}. Found headers: ${headers.join(', ')}`);
    }
    
    // Process data rows
    const salesData: SalesData[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines
      
      // Handle quoted values correctly
      let values: string[] = [];
      let inQuote = false;
      let currentValue = '';
      
      // Simple CSV parser that handles quotes
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        
        if (char === '"' && (j === 0 || line[j-1] !== '\\')) {
          inQuote = !inQuote;
        } else if (char === delimiter && !inQuote) {
          values.push(currentValue);
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // Don't forget the last value
      values.push(currentValue);
      
      // Fallback to simple split if issue with parsing
      if (values.length !== headers.length) {
        console.warn(`Line ${i+1} has ${values.length} values but headers has ${headers.length}. Falling back to simple split.`);
        values = line.split(delimiter);
      }
      
      // Clean up values
      values = values.map(v => v.trim().replace(/^"(.*)"$/, '$1'));
      
      try {
        // For Shopify format, ensure we're looking at the right columns
        const qtyIndex = columnMap.salesQuantity;
        const revenueIndex = columnMap.salesRevenue;
        
        const quantity = parseInt(values[qtyIndex], 10);
        // Handle currency formatting in revenue values
        const revenueStr = values[revenueIndex].replace(/[$,]/g, '');
        const revenue = parseFloat(revenueStr);
        
        if (isNaN(quantity) || isNaN(revenue)) {
          console.warn(`Skipping line ${i+1} due to invalid number format:`, values);
          continue;
        }
        
        // Create data object
        salesData.push({
          sku: values[columnMap.sku],
          productName: values[columnMap.productName],
          period: values[columnMap.period],
          salesQuantity: quantity,
          salesRevenue: revenue,
          // If platform column is missing, use "Unknown" or extract from filename
          platform: columnMap.platform >= 0 ? values[columnMap.platform] : "Shopify"
        });
      } catch (lineError) {
        console.error(`Error parsing line ${i+1}:`, lineError, values);
        // Continue with other lines instead of failing completely
      }
    }
    
    if (salesData.length === 0) {
      throw new Error('No valid data rows found in CSV');
    }
    
    return salesData;
  } catch (error) {
    console.error('Error parsing CSV text:', error);
    throw error;
  }
};

/**
 * Parses a CSV file containing sales data
 * @param file The CSV file to parse
 * @returns Promise that resolves to an array of SalesData objects
 */
export const parseSalesData = async (file: File): Promise<SalesData[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csvText = event.target?.result as string;
        if (!csvText) {
          throw new Error('Failed to read file');
        }
        
        const salesData = parseCSVText(csvText);
        resolve(salesData);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Generates forecast data based on historical sales data and growth rate
 * @param salesData Historical sales data
 * @param growthRate Growth rate as percentage (e.g., 5 for 5%)
 * @returns Forecast data
 */
export const generateForecasts = (salesData: SalesData[], growthRate: number): ForecastData => {
  // Get unique periods from historical data (assuming format like "2023-01")
  const periods = [...new Set(salesData.map(item => item.period))].sort();
  
  // Generate forecast periods (next 6 months from the last historical period)
  const lastPeriod = periods[periods.length - 1];
  const [lastYear, lastMonth] = lastPeriod.split('-').map(Number);
  
  const forecastPeriods: string[] = [];
  let year = lastYear;
  let month = lastMonth;
  
  for (let i = 0; i < 6; i++) {
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
    
    forecastPeriods.push(`${year}-${month.toString().padStart(2, '0')}`);
  }
  
  // Group historical data by SKU and month
  const skuMonthlyData: { [sku: string]: { [month: string]: { qty: number[], revenue: number[] } } } = {};
  
  // Initialize the structure
  salesData.forEach(item => {
    const sku = item.sku;
    // We're interested in the month portion (e.g., "01" from "2023-01")
    const month = item.period.split('-')[1];
    
    if (!skuMonthlyData[sku]) {
      skuMonthlyData[sku] = {};
    }
    
    if (!skuMonthlyData[sku][month]) {
      skuMonthlyData[sku][month] = { qty: [], revenue: [] };
    }
    
    skuMonthlyData[sku][month].qty.push(item.salesQuantity);
    skuMonthlyData[sku][month].revenue.push(item.salesRevenue);
  });
  
  // Calculate monthly averages for each SKU by month
  const skuMonthlyAvg: { [sku: string]: { [month: string]: { avgQty: number, avgRevenue: number, avgPrice: number } } } = {};
  
  Object.entries(skuMonthlyData).forEach(([sku, monthData]) => {
    skuMonthlyAvg[sku] = {};
    
    Object.entries(monthData).forEach(([month, data]) => {
      const totalQty = data.qty.reduce((sum, qty) => sum + qty, 0);
      const totalRev = data.revenue.reduce((sum, rev) => sum + rev, 0);
      const avgQty = totalQty / data.qty.length;
      const avgRevenue = totalRev / data.revenue.length;
      const avgPrice = totalQty > 0 ? totalRev / totalQty : 0;
      
      skuMonthlyAvg[sku][month] = { avgQty, avgRevenue, avgPrice };
    });
  });
  
  // Calculate forecast for each product
  const productForecasts: ForecastProduct[] = [];
  
  Object.entries(skuMonthlyAvg).forEach(([sku, monthlyAvg]) => {
    // Find product name from sales data
    const productData = salesData.find(item => item.sku === sku);
    if (!productData) return; // Skip if no data found for this SKU
    
    const productName = productData.productName;
    
    // Generate forecast for each month, using historical average for same month * growth rate
    const forecasts = forecastPeriods.map(period => {
      // Extract month number (e.g., "01" from "2024-01")
      const monthNum = period.split('-')[1];
      
      // Get all historical data points for this month across all years
      const historicalDataPoints = salesData
        .filter(item => item.period.split('-')[1] === monthNum)
        .sort((a, b) => a.period.localeCompare(b.period));

      // Calculate average of same month across all previous years
      const historicalAvg = historicalDataPoints.length > 0 
        ? historicalDataPoints.reduce((sum, item) => sum + item.salesQuantity, 0) / historicalDataPoints.length 
        : 0;
      
      // If we have historical average for this month, use it; otherwise use overall average
      let baseQty = 0;
      let basePrice = 0;
      
      if (monthlyAvg[monthNum]) {
        // We have historical data for this month
        baseQty = monthlyAvg[monthNum].avgQty;
        basePrice = monthlyAvg[monthNum].avgPrice;
      } else {
        // No historical data for this month, use average across all months
        const allAvgs = Object.values(monthlyAvg);
        const sumQty = allAvgs.reduce((sum, avg) => sum + avg.avgQty, 0);
        const sumPrice = allAvgs.reduce((sum, avg) => sum + avg.avgPrice, 0);
        baseQty = sumQty / allAvgs.length;
        basePrice = sumPrice / allAvgs.length;
      }
      
      // Apply flat growth rate
      const growthFactor = 1 + (growthRate / 100);
      const forecastQty = Math.round(baseQty * growthFactor);
      const forecastRev = basePrice * forecastQty;
      
      // Calculate YOY by comparing to average of same month across all previous years
      const yoy = calculateYOY(forecastQty, historicalAvg);
      
      return {
        period,
        salesQuantity: forecastQty,
        salesRevenue: forecastRev,
        yoy
      };
    });
    
    // Calculate forecast totals
    const forecastTotalQty = forecasts.reduce((sum, item) => sum + item.salesQuantity, 0);
    const forecastTotalRev = forecasts.reduce((sum, item) => sum + item.salesRevenue, 0);
    
    productForecasts.push({
      sku,
      productName,
      forecasts,
      totalQuantity: forecastTotalQty,
      totalRevenue: forecastTotalRev
    });
  });
  
  // Calculate monthly summaries
  const monthlySummary: MonthlySummary[] = forecastPeriods.map(period => {
    const totalQuantity = productForecasts.reduce(
      (sum, product) => sum + (product.forecasts.find(f => f.period === period)?.salesQuantity || 0),
      0
    );
    
    const totalRevenue = productForecasts.reduce(
      (sum, product) => sum + (product.forecasts.find(f => f.period === period)?.salesRevenue || 0),
      0
    );
    
    return { period, totalQuantity, totalRevenue };
  });
  
  // Calculate overall summary
  const overallTotalQty = productForecasts.reduce((sum, product) => sum + product.totalQuantity, 0);
  const overallTotalRev = productForecasts.reduce((sum, product) => sum + product.totalRevenue, 0);
  
  // Calculate YOY for each forecast period
  const lastHistoricalPeriod = periods[periods.length - 1];
  const [lastHistoricalYear, lastHistoricalMonth] = lastHistoricalPeriod.split('-').map(Number);
  const nextYear = lastHistoricalYear + 1;
  const nextPeriod = `${nextYear}-${lastHistoricalMonth.toString().padStart(2, '0')}`;

  const yoy = calculateYOY(
    salesData.filter(item => item.period === nextPeriod).reduce((sum, item) => sum + item.salesQuantity, 0),
    salesData.filter(item => item.period === lastPeriod).reduce((sum, item) => sum + item.salesQuantity, 0)
  );

  return {
    byProduct: productForecasts,
    monthlySummary,
    overallSummary: {
      totalQuantity: overallTotalQty,
      totalRevenue: overallTotalRev,
      growthRate,
      yoy
    } as OverallSummary
  };
};

/**
 * Checks if an item matches the filter criteria
 * @param text The text to check (SKU or product name)
 * @param filterTerms The filter terms (comma-separated)
 * @param filterType The type of filter to apply
 * @returns Boolean indicating if the item matches
 */
export const matchesFilterCriteria = (text: string, filterTerms: string, filterType: FilterType): boolean => {
  if (!filterTerms.trim()) return true;
  
  const textLower = text.toLowerCase();
  // Split by commas and trim each term
  const terms = filterTerms.split(',').map(term => term.trim().toLowerCase()).filter(term => term !== '');
  
  // If no valid terms, return true
  if (terms.length === 0) return true;
  
  switch (filterType) {
    case 'contains':
      // Match if ANY term is contained in the text
      return terms.some(term => textLower.includes(term));
    
    case 'not_contains':
      // Match if NONE of the terms are contained in the text
      return !terms.some(term => textLower.includes(term));
      
    case 'exact':
      // Match if ANY term is exactly the text
      return terms.some(term => textLower === term);
      
    case 'not_exact':
      // Match if NONE of the terms are exactly the text
      return !terms.some(term => textLower === term);
      
    default:
      return false;
  }
};

/**
 * Format number with commas
 * @param value The number to format
 * @param decimals The number of decimal places to include
 * @returns Formatted number string with commas
 */
export const formatNumberWithCommas = (num: number, decimals: number = 0): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format comparison text
 * @param diff The difference value
 * @param diffPercent The percentage difference
 * @param isUnits Whether the comparison is for units (true) or revenue (false)
 * @returns Formatted comparison text (e.g., "+50% YOY, +1 units")
 */
export const formatComparisonText = (diff: number, diffPercent: number, isUnits: boolean = true): string => {
  const sign = diff > 0 ? '+' : '';
  const unitText = isUnits ? ' units' : '';
  return `${sign}${diffPercent.toFixed(0)}% YOY, ${sign}${Math.abs(diff).toFixed(0)}${unitText}`;
};

/**
 * Checks if a SKU is "dead" (no sales in the past N days)
 * @param sku The SKU to check
 * @param salesData The sales data array
 * @param dayThreshold Number of days to check (default 60)
 * @returns Boolean indicating if the SKU is dead
 */
export const isDeadSku = (sku: string, salesData: SalesData[], dayThreshold: number = 60): boolean => {
  // Get the most recent date in the dataset
  const periods = [...new Set(salesData.map(item => item.period))].sort();
  const mostRecentPeriod = periods[periods.length - 1];
  
  // Convert to Date object
  const [year, month] = mostRecentPeriod.split('-').map(Number);
  const mostRecentDate = new Date(year, month - 1, 1); // month is 0-indexed in JS Date
  
  // Look for sales of this SKU within threshold
  const salesForSku = salesData.filter(item => item.sku === sku);
  
  // Check if there are any sales in the past N days
  return !salesForSku.some(item => {
    const [itemYear, itemMonth] = item.period.split('-').map(Number);
    const itemDate = new Date(itemYear, itemMonth - 1, 1);
    const daysDiff = (mostRecentDate.getTime() - itemDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= dayThreshold && item.salesQuantity > 0;
  });
};

/**
 * Checks if a SKU is an "outlier" that should be excluded by default
 * @param sku The SKU to check
 * @param productName The product name to check
 * @returns Boolean indicating if the SKU is an outlier
 */
export const isOutlierSku = (sku: string, productName: string): boolean => {
  const skuLower = sku.toLowerCase();
  const productNameLower = productName.toLowerCase();
  
  // Check for shipping protection
  if (skuLower.includes('shipping') && skuLower.includes('protection')) return true;
  
  // Check for fondu in title or SKU
  if (skuLower.includes('fondu') || productNameLower.includes('fondu')) return true;
  
  // Check for freebie or free in title
  if (productNameLower.includes('freebie') || productNameLower.includes('free')) return true;
  
  // Check for redo SKUs
  if (skuLower.includes('redo') || productNameLower.includes('redo')) return true;
  
  return false;
};

// Utility functions for forecast calculations

export const calculateSmoothedForecast = (sku: string, month: string, alpha: number = 0.3, salesData: any[]) => {
  // Get all historical data points for this SKU and month, sorted by period
  const historicalData = salesData
    .filter(item => item.sku === sku && item.period.split('-')[1] === month.split('-')[1])
    .sort((a, b) => a.period.localeCompare(b.period));

  if (historicalData.length === 0) return 0;

  // Initialize with first value
  let smoothedValue = historicalData[0].salesQuantity;
  
  // Apply exponential smoothing
  for (let i = 1; i < historicalData.length; i++) {
    smoothedValue = alpha * historicalData[i].salesQuantity + (1 - alpha) * smoothedValue;
  }

  return smoothedValue;
};

// Re-export math functions from mathUtils.ts
export * from './mathUtils'; 